import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { CircleDot, Eye, MoreHorizontal, UserPlus, UserRound } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import ListFilterBar from '../components/work/ListFilterBar';
import { PageFrame } from '../components/work/page-chrome';
import { BrandEmpty, ErrorAlert, ListEmpty, LoadingRows, PageTitle } from '../components/work/page-states';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import { useOverlays } from '../components/work/overlay-context';
import { useSession } from '../components/session-context';
import { apiErrorCode, apiErrorMessage, apiFetch, v1Path } from '../lib/api';
import { matchesQuery, selectedAllows } from '../lib/directory-filter';
import { isMemberDto, memberDisplayName, parseItems, type MemberDto } from '../lib/work-types';
import { startViewAs } from '../lib/view-as';

const MEMBER_CHIPS = ['role', 'active'] as const;
const MEMBER_DEFAULT_ORDER = 'email';
const MEMBER_SORT_OPTIONS = [
  { value: 'email', label: 'Email A-Z' },
  { value: '-email', label: 'Email Z-A' },
  { value: 'role', label: 'Role' },
] as const;

const PAGE_LIMIT = 100;

type OrgRole = 'admin' | 'member';

function isOrgRole(value: string): value is OrgRole {
  return value === 'admin' || value === 'member';
}

function isPendingInvite(value: unknown): value is { email: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { status?: unknown }).status === 'pending'
  );
}

function memberTotal(value: unknown): number | null {
  if (typeof value !== 'object' || value === null) return null;
  const page = (value as { page?: unknown }).page;
  if (typeof page !== 'object' || page === null) return null;
  const total = (page as { total?: unknown }).total;
  return typeof total === 'number' ? total : null;
}

export default function OrgMembers() {
  const { email, userId, isAdmin, viewAs } = useSession();
  const canViewAs = isAdmin && !viewAs;
  const { toast, confirm } = useOverlays();
  const filters = useDirectoryQuery(MEMBER_CHIPS, MEMBER_DEFAULT_ORDER);
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('member');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [actingUserId, setActingUserId] = useState<string | null>(null);

  const membersPath = v1Path(`/members?limit=${PAGE_LIMIT}&offset=0`);

  const visible = useMemo(() => {
    const rows = members.filter((member) => {
      if (!selectedAllows(filters.selected.role, member.role)) return false;
      const activeKey = member.is_active ? 'active' : 'inactive';
      if (!selectedAllows(filters.selected.active, activeKey)) return false;
      return matchesQuery(filters.query, [member.email, memberDisplayName(member), member.role]);
    });
    const orderBy = filters.orderBy ?? MEMBER_DEFAULT_ORDER;
    return rows.slice().sort((left, right) => {
      const leftEmail = left.email ?? left.user_id;
      const rightEmail = right.email ?? right.user_id;
      if (orderBy === '-email') return rightEmail.localeCompare(leftEmail);
      if (orderBy === 'role') return left.role.localeCompare(right.role) || leftEmail.localeCompare(rightEmail);
      return leftEmail.localeCompare(rightEmail);
    });
  }, [filters.orderBy, filters.query, filters.selected.active, filters.selected.role, members]);

  const canManage = useMemo(() => {
    if (!email) return false;
    const self = members.find((member) => member.email === email);
    return self?.role === 'admin' && self.is_active;
  }, [members, email]);
  const showActions = canManage || canViewAs;

  async function onViewAs(member: MemberDto) {
    setActingUserId(member.user_id);
    try {
      await startViewAs(member.user_id);
    } catch (err) {
      toast(apiErrorMessage(err, 'Could not switch to their view.'));
      setActingUserId(null);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<unknown>(membersPath);
      setMembers(parseItems(res, isMemberDto, 'organization members'));
      setTotal(memberTotal(res));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load organization members'));
      setMembers([]);
      setTotal(null);
    } finally {
      setLoading(false);
    }
  }, [membersPath]);

  useEffect(() => {
    void load();
  }, [load]);

  function openInvite() {
    setInviteEmail('');
    setInviteRole('member');
    setInviteError(null);
    setInviteOpen(true);
  }

  async function onInvite(event: FormEvent) {
    event.preventDefault();
    const address = inviteEmail.trim().toLowerCase();
    if (!address) {
      setInviteError('Email is required.');
      return;
    }
    setInviting(true);
    setInviteError(null);
    try {
      const res = await apiFetch<unknown>(v1Path('/members'), {
        method: 'POST',
        body: JSON.stringify({ email: address, role: inviteRole }),
      });
      setInviteOpen(false);
      toast(
        isPendingInvite(res)
          ? `Invitation sent to ${address}`
          : `${address} added to the organization`,
      );
      await load();
    } catch (err) {
      setInviteError(
        apiErrorCode(err) === 'CONFLICT'
          ? 'That person is already an organization member.'
          : apiErrorMessage(err, 'Failed to invite member'),
      );
    } finally {
      setInviting(false);
    }
  }

  async function onChangeRole(member: MemberDto, role: OrgRole) {
    if (member.role === role) return;
    setActingUserId(member.user_id);
    setError(null);
    try {
      await apiFetch(v1Path(`/members/${member.user_id}`), {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
      toast(role === 'admin' ? 'Member is now an organization admin' : 'Member role updated');
      await load();
    } catch (err) {
      const message =
        apiErrorCode(err) === 'LAST_ADMIN'
          ? 'Cannot change role: the organization must keep at least one admin.'
          : apiErrorMessage(err, 'Failed to update role');
      setError(message);
      toast(message);
    } finally {
      setActingUserId(null);
    }
  }

  async function onDeactivate(member: MemberDto) {
    const label = member.email ?? 'this member';
    const ok = await confirm({
      title: 'Deactivate member',
      description: `Deactivate ${label}? They lose access to the organization and every stream in it. Inviting the same email again restores them.`,
      confirmLabel: 'Deactivate',
      destructive: true,
    });
    if (!ok) return;
    setActingUserId(member.user_id);
    setError(null);
    try {
      await apiFetch(v1Path(`/members/${member.user_id}`), { method: 'DELETE' });
      toast('Member deactivated');
      await load();
    } catch (err) {
      const message =
        apiErrorCode(err) === 'LAST_ADMIN'
          ? 'Cannot deactivate the last organization admin.'
          : apiErrorMessage(err, 'Failed to deactivate member');
      setError(message);
      toast(message);
    } finally {
      setActingUserId(null);
    }
  }

  const inviteButton = (
    <Button type="button" size="sm" onClick={openInvite}>
      <UserPlus className="mr-1.5 h-4 w-4" />
      Invite member
    </Button>
  );

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
      <PageTitle
        title="Members"
        count={loading || error ? undefined : visible.length}
        action={canManage ? inviteButton : null}
      />
      <p className="text-sm text-muted-foreground">
        Everyone with access to this organization. Organization membership does not grant access to
        a stream — add people to streams individually.
      </p>
      <ListFilterBar
        query={filters.query}
        onQueryChange={(query) => filters.apply({ query })}
        searchPlaceholder="Search email or name..."
        searchAriaLabel="Search members"
        chips={[
          {
            id: 'role',
            label: 'Role',
            icon: UserRound,
            options: [
              { value: 'admin', label: 'Admin' },
              { value: 'member', label: 'Member' },
            ],
            selected: filters.selected.role,
            onToggle: (value) => filters.toggleChip('role', value),
          },
          {
            id: 'active',
            label: 'Status',
            icon: CircleDot,
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ],
            selected: filters.selected.active,
            onToggle: (value) => filters.toggleChip('active', value),
          },
        ]}
        sort={{
          value: filters.orderBy ?? MEMBER_DEFAULT_ORDER,
          options: [...MEMBER_SORT_OPTIONS],
          onChange: (value) => filters.apply({ orderBy: value }),
        }}
        onClear={filters.clear}
      />
      {error ? <ErrorAlert>{error}</ErrorAlert> : null}
      {total !== null && total > members.length ? (
        <p className="text-sm text-muted-foreground">
          Showing the first {members.length} of {total} members.
        </p>
      ) : null}
      {loading ? (
        <LoadingRows columns={3} />
      ) : members.length === 0 ? (
        <BrandEmpty title="No organization members." />
      ) : visible.length === 0 ? (
        <ListEmpty
          title="No members match"
          description="Clear filters if you expected matches."
          action={
            <Button type="button" variant="outline" size="sm" onClick={filters.clear}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-0">Email</TableHead>
              <TableHead className="w-32">Role</TableHead>
              {showActions ? <TableHead className="w-20 text-right">Actions</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((member) => (
              <TableRow key={member.user_id}>
                <TableCell className="min-w-0 truncate text-sm">{member.email ?? member.user_id}</TableCell>
                <TableCell>
                  {member.role === 'admin' ? (
                    <Badge>Admin</Badge>
                  ) : (
                    <Badge variant="secondary">Member</Badge>
                  )}
                </TableCell>
                {showActions ? (
                  <TableCell className="text-right">
                    {canManage || (member.is_active && member.user_id !== userId) ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={actingUserId === member.user_id}
                            aria-label={`Actions for ${member.email ?? member.user_id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canViewAs && member.is_active && member.user_id !== userId ? (
                            <>
                              <DropdownMenuItem onSelect={() => void onViewAs(member)}>
                                <Eye />
                                View as {memberDisplayName(member)}
                              </DropdownMenuItem>
                              {canManage ? <DropdownMenuSeparator /> : null}
                            </>
                          ) : null}
                          {canManage && member.role !== 'admin' ? (
                            <DropdownMenuItem onSelect={() => void onChangeRole(member, 'admin')}>
                              Make organization admin
                            </DropdownMenuItem>
                          ) : null}
                          {canManage && member.role !== 'member' ? (
                            <DropdownMenuItem onSelect={() => void onChangeRole(member, 'member')}>
                              Make member
                            </DropdownMenuItem>
                          ) : null}
                          {canManage ? (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => void onDeactivate(member)}
                            >
                              Deactivate
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite member</DialogTitle>
            <DialogDescription>
              People who already have an account join immediately. Anyone else is emailed an
              invitation that expires in seven days.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onInvite} className="space-y-4">
            {inviteError ? <ErrorAlert>{inviteError}</ErrorAlert> : null}
            <div className="grid gap-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                autoComplete="off"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={inviteRole}
                onValueChange={(value) => {
                  if (isOrgRole(value)) setInviteRole(value);
                }}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviting}>
                {inviting ? 'Sending...' : 'Send invite'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageFrame>
  );
}
